import { destroyCategories, useCategories } from '@gozoki/api'
import { Button, TextField } from '@mui/material'
import { useMemo, useState } from 'react'

import { includesLowerCase } from '@gozoki/tools'
import type { GridColDef } from '@mui/x-data-grid'
import { useMutation } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useRights } from '../../../utils/hooks'
import { ConfirmResourceDeletionButton } from '../../communication/ConfirmDeletionButton'
import SimpleDataGrid from '../../containers/SimpleDataGrid'
import Thumbnail from '../../icons/Thumbnail'

const categoriesColDef: GridColDef[] = [
  {
    field: 'image',
    headerName: 'Image',
    renderCell: (url) => <Thumbnail url={url.value} alt="Catégorie" rounded />,
    width: 150,
  },
  { field: 'name', headerName: 'Nom', width: 200 },
]

export const CategoriesPanel = () => {
  const navigate = useNavigate()
  const isUserAdmin = useRights(['ADMIN'])

  const [categorySearch, setCategorySearch] = useState('')
  const [categoriesSelection, setCategoriesSelection] = useState<number[]>([])

  const categories = useCategories()

  // Transform fetched categories into DataGrid format
  const categoriesRows = useMemo(() => {
    if (categories.data === undefined) {
      return []
    }
    return categories.data
      .filter((category) => includesLowerCase(category.name, categorySearch))
      .map((category) => ({
        id: category.id,
        image: category.image?.url,
        name: category.name,
      }))
  }, [categories.data, categorySearch])

  const deleteCategoryMut = useMutation({
    mutationFn: destroyCategories,
  })

  const handleDeleteCategory = () => {
    const deletionPromise = deleteCategoryMut.mutateAsync(categoriesSelection, {
      onSuccess: () => {
        categories.refetch()
      },
    })
    setCategoriesSelection([]) // Avoid disabled button flickering
    return deletionPromise
  }

  const navigateAndUpdateCategory = () => {
    navigate({ search: { categoryId: categoriesSelection[0] }, to: '/categories/new' })
  }

  const categoriesDisabled = categoriesSelection.length === 0 || deleteCategoryMut.isPending

  return (
    <>
      <div style={{ alignItems: 'center', display: 'flex', padding: '16px' }}>
        <TextField
          variant="outlined"
          label="Recherche"
          value={categorySearch}
          onChange={(e) => setCategorySearch(e.target.value)}
        />
        <div style={{ flexGrow: 1 }} />
        <ConfirmResourceDeletionButton
          handleResourceDeletion={handleDeleteCategory}
          disabled={!isUserAdmin ? true : categoriesDisabled}
        />
        <Button
          variant="contained"
          sx={{ height: '36px' }}
          disabled={categoriesDisabled}
          onClick={navigateAndUpdateCategory}
        >
          {isUserAdmin ? 'Modifier' : 'Voir'}
        </Button>
      </div>
      <SimpleDataGrid
        rowHeight={100} // For image display
        columns={categoriesColDef}
        rows={categoriesRows}
        loading={categories.isLoading || deleteCategoryMut.isPending || categories.isRefetching}
        onRowSelectionModelChange={(newSelection) =>
          setCategoriesSelection(newSelection as number[])
        }
      />
    </>
  )
}
